import React, { useState, useEffect, useCallback } from "react";
import { ADMIN_API_BASE_URL, currentAppMode } from "../config";
import { motion, AnimatePresence } from "framer-motion";

// Import icons individually to avoid TypeScript issues
import { FaGripVertical } from "react-icons/fa";
import { FaEye } from "react-icons/fa";
import { FaEyeSlash } from "react-icons/fa";
import { FaEdit } from "react-icons/fa";
import { FaTrash } from "react-icons/fa";
import { FaExclamationTriangle } from "react-icons/fa";
import { FaArrowLeft } from "react-icons/fa";
import { FaSync } from "react-icons/fa";
import { FaPlus } from "react-icons/fa";
import DeleteModal from "../common/deleteModal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import BannerForm from "./BannerForm";
import MainHeader from "../components/MainHeader";

interface Banner {
  id: string;
  internal_name: string;
  headline: string;
  sub_headline: string;
  cta_text: string;
  image_url: string;
  status: "ACTIVE" | "INACTIVE";
  display_order: number;
  link_type: "CHALLENGE" | "CUSTOM_URL";
  link_target: string;
  appType: string;
  created_at: string;
  updated_at: string;
}

interface BannerManagementProps {
  onBack: () => void;
}

// Sortable Banner Item Component
const SortableBannerItem: React.FC<{
  banner: Banner;
  onStatusToggle: (bannerId: string, currentStatus: string) => void;
  onEdit: (banner: Banner) => void;
  onDelete: (bannerId: string) => void;
  getStatusBadge: (status: string) => React.ReactNode;
  getLinkTypeBadge: (linkType: string) => React.ReactNode;
}> = ({
  banner,
  onStatusToggle,
  onEdit,
  onDelete,
  getStatusBadge,
  getLinkTypeBadge,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: banner.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`${
        isDragging ? "bg-blue-50 shadow-lg" : "bg-white"
      } hover:bg-gray-50 transition-colors duration-200`}
    >
      <div className="px-4 py-4 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            >
              <FaGripVertical className="w-4 h-4" />
            </div>

            {/* Banner Image Thumbnail */}
            <div className="flex-shrink-0">
              <img
                src={banner.image_url}
                alt={banner.headline}
                className="h-16 w-24 object-cover rounded-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/image.png";
                }}
              />
            </div>

            {/* Banner Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="text-sm font-medium text-gray-900 truncate">
                  {banner.internal_name}
                </h3>
                {getStatusBadge(banner.status)}
                {getLinkTypeBadge(banner.link_type)}
              </div>
              <p className="text-sm text-gray-900 font-medium mb-1">
                {banner.headline}
              </p>
              {banner.sub_headline && (
                <p className="text-sm text-gray-500 mb-1">
                  {banner.sub_headline}
                </p>
              )}
              <p className="text-sm text-gray-500">
                CTA: "{banner.cta_text}" → {banner.link_target}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Order: {banner.display_order} • Updated:{" "}
                {new Date(banner.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onStatusToggle(banner.id, banner.status)}
              className={`inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded ${
                banner.status === "ACTIVE"
                  ? "text-red-700 bg-red-100 hover:bg-red-200"
                  : "text-green-700 bg-green-100 hover:bg-green-200"
              }`}
            >
              {banner.status === "ACTIVE" ? (
                <FaEyeSlash className="w-3 h-3 mr-1" />
              ) : (
                <FaEye className="w-3 h-3 mr-1" />
              )}
              {banner.status === "ACTIVE" ? "Deactivate" : "Activate"}
            </button>
            <button
              onClick={() => onEdit(banner)}
              className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
            >
              <FaEdit className="w-3 h-3 mr-1" />
              Edit
            </button>
            <button
              onClick={() => onDelete(banner.id)}
              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
            >
              <FaTrash className="w-3 h-3 mr-1" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </li>
  );
};

const BannerManagement: React.FC<BannerManagementProps> = ({ onBack }) => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | undefined>(
    undefined
  );
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [reordering, setReordering] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState<Banner | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        setError("Authentication required");
        return;
      }

      const params = new URLSearchParams();
      if (statusFilter !== "all") {
        params.append("status_filter", statusFilter);
      }

      const response = await fetch(
        `${ADMIN_API_BASE_URL}/banners?${params}&appType=${currentAppMode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch banners");
      }

      const data = await response.json();
      setBanners(data.banners || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleStatusToggle = async (
    bannerId: string,
    currentStatus: string
  ) => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        setError("Authentication required");
        return;
      }

      const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";

      const response = await fetch(
        `${ADMIN_API_BASE_URL}/banners/${bannerId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: newStatus,
            appType: currentAppMode,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update banner status");
      }

      // Update local state
      setBanners((prev) =>
        prev.map((banner) =>
          banner.id === bannerId
            ? { ...banner, status: newStatus as "ACTIVE" | "INACTIVE" }
            : banner
        )
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update banner status"
      );
    }
  };

  const handleDelete = (bannerId: string) => {
    const banner = banners.find((b) => b.id === bannerId);
    if (banner) {
      setDeletingBanner(banner);
      setDeleteModal(true);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setDeletingBanner(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingBanner) return;

    try {
      setDeleteLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        setError("Authentication required");
        return;
      }

      const response = await fetch(
        `${ADMIN_API_BASE_URL}/banners/${deletingBanner.id}?appType=${currentAppMode}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete banner");
      }

      // Remove from local state
      setBanners((prev) =>
        prev.filter((banner) => banner.id !== deletingBanner.id)
      );
      handleCloseDeleteModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete banner");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setBanners((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update display order
        const updatedItems = newItems.map((item, index) => ({
          ...item,
          display_order: index + 1,
        }));

        // Save the new order
        saveNewOrder(updatedItems);

        return updatedItems;
      });
    }
  };

  const saveNewOrder = async (updatedItems: Banner[]) => {
    setReordering(true);

    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        setError("Authentication required");
        return;
      }

      const bannerOrders = updatedItems.map((banner, index) => ({
        id: banner.id,
        display_order: index + 1,
      }));

      const response = await fetch(`${ADMIN_API_BASE_URL}/banners/reorder`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          banner_orders: bannerOrders,
          appType: currentAppMode,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to reorder banners");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to reorder banners"
      );
      // Revert to original order
      fetchBanners();
    } finally {
      setReordering(false);
    }
  };

  const handleEdit = (banner: Banner) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingBanner(undefined);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    fetchBanners();
  };
  const [bannerLoading, setBannerLoading] = useState(false);
  const handleSaveBanner = async (
    bannerData: Omit<
      Banner,
      "id" | "created_at" | "updated_at" | "display_order"
    >
  ) => {
    try {
      setBannerLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        throw new Error("Authentication required");
      }

      const url = editingBanner
        ? `${ADMIN_API_BASE_URL}/banners/${editingBanner.id}`
        : `${ADMIN_API_BASE_URL}/banners`;

      const method = editingBanner ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...bannerData,
          appType: currentAppMode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to save banner");
      }

      handleFormSuccess();
    } catch (error) {
      console.error("Error saving banner:", error);
      // You might want to show an error message to the user here
    } finally {
      setBannerLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config = {
      ACTIVE: { color: "bg-green-100 text-green-800", icon: FaEye },
      INACTIVE: { color: "bg-gray-100 text-gray-800", icon: FaEyeSlash },
    };

    const statusConfig =
      config[status as keyof typeof config] || config.INACTIVE;
    const IconComponent = statusConfig.icon as React.ComponentType<{
      className?: string;
    }>;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}
      >
        <IconComponent className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getLinkTypeBadge = (linkType: string) => {
    const config = {
      CHALLENGE: { color: "bg-blue-100 text-blue-800", text: "Challenge" },
      CUSTOM_URL: {
        color: "bg-purple-100 text-purple-800",
        text: "Custom URL",
      },
    };

    const typeConfig =
      config[linkType as keyof typeof config] || config.CUSTOM_URL;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeConfig.color}`}
      >
        {typeConfig.text}
      </span>
    );
  };

  if (showForm) {
    return (
      <BannerForm
        banner={editingBanner}
        onSave={handleSaveBanner}
        onCancel={handleFormClose}
        loading={bannerLoading}
        error=""
      />
    );
  }

  // if (loading && banners.length === 0) {
  //   return (
  //     <div className="min-h-screen bg-gray-50 py-8">
  //       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
  //         <div className="flex justify-center items-center h-64">
  //           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FaExclamationTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading banners
                </h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <MainHeader onBackToDashboard={onBack || (() => {})} />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div>
                  <motion.button
                    onClick={onBack}
                    className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors duration-200"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    Back to Dashboard
                  </motion.button>

                  <motion.h1
                    className="text-3xl font-bold text-gray-900"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    Home Page Banner
                  </motion.h1>
                  <motion.p
                    className="mt-2 text-sm text-gray-600"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    Manage home page banners
                  </motion.p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={fetchBanners}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <FaSync
                    className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button>
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FaPlus className="w-4 h-4 mr-2" />
                  Add New Banner
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">
                Status Filter:
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Banners</option>
                <option value="ACTIVE">Active Only</option>
                <option value="INACTIVE">Inactive Only</option>
              </select>
            </div>
          </div>

          {/* Banners List */}
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading banners...</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={banners.map((banner) => banner.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="divide-y divide-gray-200">
                    {banners.map((banner) => (
                      <SortableBannerItem
                        key={banner.id}
                        banner={banner}
                        onStatusToggle={handleStatusToggle}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        getStatusBadge={getStatusBadge}
                        getLinkTypeBadge={getLinkTypeBadge}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>

              {banners.length === 0 && !loading && (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">🎨</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No banners found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {statusFilter === "all"
                      ? "Get started by creating your first promotional banner."
                      : `No ${statusFilter.toLowerCase()} banners found.`}
                  </p>
                  {statusFilter === "all" && (
                    <button
                      onClick={() => setShowForm(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <FaPlus className="w-4 h-4 mr-2" />
                      Create First Banner
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Reordering Indicator */}
          {reordering && (
            <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-md shadow-lg">
              <div className="flex items-center">
                <FaSync className="w-4 h-4 mr-2 animate-spin" />
                Updating banner order...
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteModal && (
            <DeleteModal
              onCancel={handleCloseDeleteModal}
              onSubmit={handleConfirmDelete}
              loading={deleteLoading}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BannerManagement;
